import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { Observable, of, switchMap, catchError, map } from 'rxjs';
import { Globals } from '@core/globals';
import { CarService } from '@services/car.service';
import { OnlineFormService } from '@services/online-form.service';
import { CarItem } from '@models/car.model';
import { OnlineFormAccessoriesResponseProcessed } from '@models/online-form-accessories.model';

export interface OnlineFormData {
  carData: CarItem;
  accessories: OnlineFormAccessoriesResponseProcessed;
}

export const onlineFormResolver: ResolveFn<Observable<OnlineFormData | null>> = (route, state) => {
  const carId = route.queryParamMap.get('car');
  const onlineFormService = inject(OnlineFormService);
  const carService = inject(CarService);
  const router = inject(Router);
  const globals = inject(Globals);

  const currentPath = state.url.split('?')[0];
  const formData = onlineFormService.formData();

  if (!carId) {
    return of(null);
  }

  if (formData) {
    if (currentPath.includes('/online-prodej/dodani-vozu') && !formData.paymentType) {
      router.navigate([globals.routes()['onlineFormPaymentOption'].url], { queryParams: { car: carId } });
      return of(null);
    }

    if (currentPath.includes('/online-prodej/prislusenstvi') && (!formData.paymentType || !formData.deliveryType)) {
      const lastValidRoute = formData.paymentType ? 'onlineFormDeliveryType' : 'onlineFormPaymentOption';
      router.navigate([globals.routes()[lastValidRoute].url], { queryParams: { car: carId } });
      return of(null);
    }

    if (currentPath.includes('/online-prodej/kontaktni-udaje') && (!formData.paymentType || !formData.deliveryType)) {
      let lastValidRoute = 'onlineFormPaymentOption';
      if (formData.paymentType) {
        lastValidRoute = 'onlineFormDeliveryType';
        if (formData.deliveryType) {
          lastValidRoute = 'onlineFormAccessories';
        }
      }
      router.navigate([globals.routes()[lastValidRoute].url], { queryParams: { car: carId } });
      return of(null);
    }
  }

  return carService.getCarData(Number(carId)).pipe(
    switchMap((carData) => {
      if (carData && carData.aaaid) {
        return onlineFormService.fetchAccessories(carData.aaaid).pipe(
          map((accessories) => ({
            carData,
            accessories,
          })),
          catchError(() => {
            return of(null);
          }),
        );
      }
      return of(null);
    }),
    catchError((error) => {
      console.error('Error loading car data:', error);
      return of(null);
    }),
  );
};
